import { motion } from "framer-motion";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { CircleScribble, Underline } from "@/components/Annotation";

/**
 * Reusable editorial hero used across non-landing pages.
 * Signals hierarchy the same way as the coaching Hero without repeating
 * the "REHEARSAL #01 / STAGE MAP" notebook signature — that belongs only
 * to the coaching service page.
 */
export const PageHero = ({
  eyebrow,
  headline,
  emphasis,
  emphasisAccent = "circle", // "circle" | "underline" | "none"
  intro,
  meta,
  children,
  testId = "page-hero",
  // Override hook for a single page's heading scale only (e.g. a
  // fallback-webfont compensation for one language) — every other
  // PageHero usage keeps the one shared default below untouched.
  headingSizeClassName = "text-[2.5rem] sm:text-5xl md:text-6xl lg:text-[5.2rem]",
}) => {
  return (
    <section
      className="relative z-10 pt-32 md:pt-40 pb-14 md:pb-20 overflow-hidden"
      data-testid={testId}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-8">
          {eyebrow && (
            <Reveal>
              <p className="font-body text-xs uppercase tracking-[0.28em] text-terracotta mb-6 flex items-center gap-3">
                <span className="inline-block w-10 h-px bg-terracotta" />
                {eyebrow}
              </p>
            </Reveal>
          )}
          <h1 className={`font-serif ${headingSizeClassName} leading-[1.02] tracking-tight text-ink`}>
            <MaskedLines lines={Array.isArray(headline) ? headline : [headline]} delay={0} />
            {emphasis && (
              <span className="relative inline-block mt-2">
                <MaskedLines
                  lines={[emphasis]}
                  delay={0.15}
                  lineClassName="italic text-terracotta"
                />
                {emphasisAccent === "circle" && (
                  <CircleScribble
                    className="absolute -left-6 -top-3 w-[115%] h-[160%] pointer-events-none"
                    delay={0.5}
                    pathClass="hero-scribble"
                  />
                )}
                {emphasisAccent === "underline" && (
                  <Underline
                    className="absolute -bottom-2 left-0 w-full h-3"
                    delay={0.5}
                  />
                )}
              </span>
            )}
          </h1>
          {intro && (
            <Reveal delay={0.3}>
              <p className="mt-8 font-body text-lg md:text-xl text-ink-soft leading-relaxed max-w-[52ch]">
                {intro}
              </p>
            </Reveal>
          )}
          {children && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-5 flex-wrap"
            >
              {children}
            </motion.div>
          )}
        </div>
        {meta && (
          <Reveal delay={0.55} className="lg:col-span-4">
            <div className="font-hand text-terracotta text-2xl md:text-3xl leading-tight">
              {meta}
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
};
